<template>
  <div class="page">
    <div class="bg-grid" />
    <TopBar />
    <ProjectNav :project="project" />

    <main class="content">
      <!-- No pipeline -->
      <div v-if="!active_pipeline" class="empty-state">
        <p class="empty-title">No active pipeline</p>
        <p class="empty-body">
          Bootstrap a pipeline from your project description using AI.
          Make sure you have an AI client assigned in Settings first.
        </p>
        <button
          class="bootstrap-btn"
          :disabled="bootstrapping"
          @click="triggerBootstrap"
        >
          {{ bootstrapping ? "BOOTSTRAPPING…" : "BOOTSTRAP PIPELINE" }}
        </button>
        <JobPoller
          v-if="bootstrapJobId"
          :job-id="bootstrapJobId"
          done-message="Pipeline created — reloading…"
          @done="onBootstrapDone"
          @failed="onBootstrapFailed"
        />
        <p v-if="bootstrapError" class="bootstrap-error">{{ bootstrapError }}</p>
      </div>

      <template v-else>
        <!-- Pipeline header -->
        <div class="pipeline-header">
          <div class="pipeline-meta">
            <span class="pipeline-name">{{ active_pipeline.name }}</span>
            <span class="version-badge">v{{ active_pipeline.version }}</span>
            <span class="creator-badge">{{ active_pipeline.created_by }}</span>
          </div>
          <div class="pipeline-versions">
            <span v-for="p in pipelines" :key="p.id" class="version-pill" :class="{ active: p.is_active }">
              v{{ p.version }}
            </span>
          </div>
        </div>

        <!-- Steps -->
        <div class="steps-list">
          <div v-for="step in active_pipeline.steps" :key="step.id" class="step-card">
            <div class="step-header">
              <div class="step-order">{{ step.order }}</div>
              <div class="step-info">
                <span class="step-name">{{ step.name }}</span>
                <span v-if="step.concurrent" class="concurrent-badge">CONCURRENT</span>
              </div>
            </div>

            <p v-if="step.description" class="step-desc">{{ step.description }}</p>

            <!-- Active prompt -->
            <div v-if="step.active_prompt" class="prompt-block">
              <div class="prompt-header">
                <span class="block-label">ACTIVE PROMPT</span>
                <span class="version-chip">v{{ step.active_prompt.version }}</span>
                <span
                  v-if="step.active_prompt.eval_score > 0"
                  class="eval-chip"
                  :style="{ color: scoreColor(step.active_prompt.eval_score) }"
                >
                  {{ (step.active_prompt.eval_score * 100).toFixed(1) }}%
                </span>
              </div>
              <pre class="prompt-content">{{ truncate(step.active_prompt.content, 300) }}</pre>
            </div>

            <!-- Feedback loops -->
            <div v-if="step.feedback_loops.length > 0" class="feedback-loops">
              <span class="block-label">FEEDBACK LOOPS</span>
              <div
                v-for="fl in step.feedback_loops"
                :key="fl.id"
                class="feedback-item"
              >
                <span class="fl-order">{{ fl.order }}</span>
                <span class="fl-name">{{ fl.name }}</span>
                <span class="fl-iterations">feedback loop</span>
              </div>
            </div>
          </div>
        </div>
      </template>
    </main>
  </div>
</template>

<script setup>
import { ref } from "vue"
import { router } from "@inertiajs/vue3"
import TopBar from "../../components/TopBar.vue"
import ProjectNav from "../../components/ProjectNav.vue"
import JobPoller from "../../components/JobPoller.vue"

const props = defineProps({
  project:         { type: Object, required: true },
  pipelines:       { type: Array,  default: () => [] },
  active_pipeline: { type: Object, default: null },
})

const bootstrapping   = ref(false)
const bootstrapJobId  = ref(null)
const bootstrapError  = ref(null)

async function triggerBootstrap() {
  bootstrapping.value  = true
  bootstrapError.value = null
  bootstrapJobId.value = null

  try {
    const token = document.querySelector("meta[name='csrf-token']")?.content
    const res   = await fetch(`/api/v1/projects/${props.project.uuid}/bootstrap`, {
      method:  "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
    })
    const data = await res.json()

    if (res.ok) {
      bootstrapJobId.value = data.job_id
    } else {
      bootstrapError.value = data.message || "Failed to start bootstrap"
      bootstrapping.value  = false
    }
  } catch {
    bootstrapError.value = "Network error"
    bootstrapping.value  = false
  }
}

function onBootstrapDone() {
  setTimeout(() => router.reload(), 1000)
}

function onBootstrapFailed(data) {
  bootstrapError.value = data.error || "Bootstrap failed"
  bootstrapping.value  = false
}

function truncate(text, max) {
  if (!text) return ""
  return text.length > max ? text.slice(0, max) + "…" : text
}

function scoreColor(score) {
  if (score >= 0.9) return "#4ade80"
  if (score >= 0.8) return "#f5a623"
  return "#f87171"
}
</script>

<style scoped>
.page {
  min-height: 100vh;
  background-color: #0a0a0b;
  position: relative;
}

.bg-grid {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  background-image:
    linear-gradient(rgba(245, 166, 35, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(245, 166, 35, 0.03) 1px, transparent 1px);
  background-size: 40px 40px;
}

.content {
  position: relative;
  z-index: 1;
  padding: 20px 24px;
}

.empty-state {
  padding: 48px;
  text-align: center;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.025);
  max-width: 480px;
}

.bootstrap-btn {
  margin-top: 20px;
  padding: 9px 20px;
  background: #f5a623;
  border: none;
  border-radius: 4px;
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1.2px;
  color: #0a0a0b;
  cursor: pointer;
  transition: opacity 0.2s;
}

.bootstrap-btn:hover:not(:disabled) { opacity: 0.85; }
.bootstrap-btn:disabled { opacity: 0.4; cursor: not-allowed; }

.bootstrap-error {
  margin-top: 12px;
  font-family: var(--font-mono);
  font-size: 11px;
  color: #f87171;
}

.empty-title {
  font-family: var(--font-display);
  font-size: 16px;
  font-weight: 700;
  color: #e8e6e3;
  margin: 0 0 8px;
}

.empty-body {
  font-family: var(--font-mono);
  font-size: 12px;
  color: rgba(232, 230, 227, 0.45);
  margin: 0;
}

.pipeline-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}

.pipeline-meta {
  display: flex;
  align-items: center;
  gap: 10px;
}

.pipeline-name {
  font-family: var(--font-display);
  font-size: 16px;
  font-weight: 700;
  color: #e8e6e3;
}

.version-badge,
.creator-badge {
  font-family: var(--font-mono);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  padding: 2px 7px;
  border-radius: 3px;
  background: rgba(245, 166, 35, 0.18);
  color: #f5a623;
}

.creator-badge {
  background: rgba(96, 165, 250, 0.15);
  color: #60a5fa;
}

.pipeline-versions {
  display: flex;
  gap: 4px;
}

.version-pill {
  font-family: var(--font-mono);
  font-size: 9px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 3px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  color: rgba(232, 230, 227, 0.25);
}

.version-pill.active {
  border-color: rgba(245, 166, 35, 0.18);
  color: #f5a623;
}

.steps-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.step-card {
  background: rgba(255, 255, 255, 0.025);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  overflow: hidden;
}

.step-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 18px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.step-order {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(245, 166, 35, 0.18);
  color: #f5a623;
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.step-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.step-name {
  font-family: var(--font-mono);
  font-size: 13px;
  font-weight: 600;
  color: #e8e6e3;
}

.concurrent-badge {
  font-family: var(--font-mono);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.8px;
  padding: 2px 6px;
  border-radius: 3px;
  background: rgba(192, 132, 252, 0.15);
  color: #c084fc;
}

.step-desc {
  font-family: var(--font-body);
  font-size: 12px;
  color: rgba(232, 230, 227, 0.45);
  padding: 12px 18px 0;
  margin: 0;
}

.prompt-block {
  margin: 12px 18px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 4px;
  overflow: hidden;
}

.prompt-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.02);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.block-label {
  font-family: var(--font-mono);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 1.2px;
  color: rgba(232, 230, 227, 0.25);
  flex: 1;
}

.version-chip,
.eval-chip {
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 600;
}

.version-chip {
  color: rgba(232, 230, 227, 0.45);
}

.prompt-content {
  padding: 12px;
  font-family: var(--font-mono);
  font-size: 11px;
  color: rgba(232, 230, 227, 0.7);
  white-space: pre-wrap;
  word-break: break-word;
  margin: 0;
  line-height: 1.6;
}

.feedback-loops {
  margin: 0 18px 14px;
}

.feedback-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
}

.fl-order {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.06);
  font-family: var(--font-mono);
  font-size: 10px;
  color: rgba(232, 230, 227, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.fl-name {
  font-family: var(--font-mono);
  font-size: 11px;
  color: #e8e6e3;
  flex: 1;
}

.fl-iterations {
  font-family: var(--font-mono);
  font-size: 10px;
  color: rgba(232, 230, 227, 0.25);
}
</style>
